/**
 * The four-cell summary derived from a profile (wireframe §5a) — the same
 * readings the server's checks make (src/verify/checks.ts), computed on the
 * client from the served payloads so the profile answers "real, taxed, VAT,
 * current?" before any record is issued. Pure; unit-tested.
 */
import { formatDate, formatRead, plural } from '../../lib/format.js';
import type { CheckStatus, EntityProfile } from '../../lib/types.js';

export const VAT_TAX_TYPE = 'Value Added Tax';

export interface SummaryReading {
  status: CheckStatus;
  title: string;
  fact: string;
  source: string;
}

export function registrySummary(p: EntityProfile, now = new Date()): SummaryReading {
  const r = p.registry;
  if (!r)
    return {
      status: 'fail',
      title: 'Not on the register',
      fact: 'No PACRA record linked',
      source: 'PACRA',
    };
  const status = (r.payload.entityStatus ?? '').trim();
  const active = status.toLowerCase() === 'active';
  const since = r.payload.registrationDate
    ? formatDate(r.payload.registrationDate)
    : null;
  return {
    status: active ? 'pass' : 'fail',
    title: active
      ? 'Registered'
      : status
        ? `Registry: ${status}`
        : 'Registry status unknown',
    fact: active
      ? since
        ? `Active since ${since}`
        : 'Active'
      : since
        ? `Registered ${since}`
        : status || '—',
    source: `PACRA · ${formatRead(r.provenance.fetched_at, now)}`,
  };
}

/**
 * The tax cell reads the server's reading (owner fix 2026-09-22): the label
 * and check the API derived — "Registration incomplete" is amber, "Owner's
 * TPIN needed" is the neutral unknown glyph (a pending human step, not a
 * finding), and a raw ZRA enum is never shown.
 */
export function taxSummary(p: EntityProfile, now = new Date()): SummaryReading {
  const t = p.tax;
  if (t.owner_tpin_required) {
    return {
      status: 'unknown',
      title: t.status_label,
      fact: "Taxed under the owner's personal TPIN — link it on the Tax tab",
      source: 'ZRA',
    };
  }
  if (!t.payload || !t.provenance) {
    return {
      status: t.check,
      title: t.status_label,
      fact: 'No ZRA record linked by name or TPIN',
      source: 'ZRA',
    };
  }
  const types = t.payload.taxTypes ?? [];
  const owner = t.basis === 'owner_tpin';
  return {
    status: t.check,
    title: t.check === 'pass' ? 'Tax registered' : t.status_label,
    fact:
      t.check === 'pass'
        ? `${owner ? "Owner's TPIN · " : ''}${plural(types.length, 'tax type')}`
        : t.status_meaning,
    source: `ZRA · ${formatRead(t.provenance.fetched_at, now)}`,
  };
}

export function vatSummary(p: EntityProfile, now = new Date()): SummaryReading {
  const t = p.tax;
  if (t.owner_tpin_required) {
    return {
      status: 'unknown',
      title: 'VAT unknown',
      fact: "Link the owner's TPIN to read the tax types",
      source: 'ZRA',
    };
  }
  if (!t.payload || !t.provenance) {
    return {
      status: 'attention',
      title: 'VAT unknown',
      fact: 'No tax record to read',
      source: 'ZRA',
    };
  }
  const registered = (t.payload.taxTypes ?? []).some((x) => x.trim() === VAT_TAX_TYPE);
  return {
    status: registered ? 'pass' : 'attention',
    title: registered ? 'VAT registered' : 'Not VAT registered',
    fact: registered ? 'Can issue Smart Invoices' : 'VAT is not among the tax types',
    source: `ZRA · ${formatRead(t.provenance.fetched_at, now)}`,
  };
}

export function returnsSummary(p: EntityProfile, now = new Date()): SummaryReading {
  const r = p.registry;
  if (!r)
    return {
      status: 'attention',
      title: 'Returns unknown',
      fact: 'No PACRA record to read',
      source: 'PACRA',
    };
  const current = r.payload.annualReturnStatus === true;
  const madeUpTo = r.payload.annualReturnMadeUpTo;
  return {
    status: current ? 'pass' : 'attention',
    title: current ? 'Returns current' : 'Returns not current',
    fact: current
      ? 'Annual return filed'
      : madeUpTo
        ? `Made up to ${formatDate(madeUpTo)}`
        : (r.payload.annualReturnReason ?? 'Not filed'),
    source: `PACRA · ${formatRead(r.provenance.fetched_at, now)}`,
  };
}

export function summaryOf(p: EntityProfile, now = new Date()): SummaryReading[] {
  return [
    registrySummary(p, now),
    taxSummary(p, now),
    vatSummary(p, now),
    returnsSummary(p, now),
  ];
}

export function yesNo(v: boolean | undefined, reason: string | null | undefined): string {
  if (v === undefined) return '—';
  return v ? 'Compliant' : reason ? `Not compliant — ${reason}` : 'Not compliant';
}
